import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { debounce, once } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import themed from 'webapp/decorators/themed';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import FormInputComponent from 'webapp/components/form/input';

export interface FormInputDurationComponentArgs {
	value: string | null;
	onChange: (value: number | null) => void;
	maxLength?: number;
	name?: string;
	forceUpdateFromValue?: boolean;
	size?: string;
	theme?: string;
	isDisabled?: boolean;
	isRequired?: boolean;
}

interface FormInputDurationComponentInterface<T> {
	Element: HTMLDivElement; // this is needed for ...attributes
	Args: T;
}

@themed()
export default class FormInputDurationComponent<
	T extends FormInputDurationComponentArgs,
> extends Component<FormInputDurationComponentInterface<T>> {
	declare themeClass: string; // from @themed

	@tracked days: number | string = 0;
	@tracked hours: number | string = 0;
	@tracked minutes: number | string = 0;
	@tracked seconds: number | string = 0;

	updateValue() {
		const m = moment.duration({
			days: +this.days || 0,
			hours: +this.hours || 0,
			minutes: +this.minutes || 0,
			seconds: +this.seconds || 0,
		});
		this.args.onChange(m.asSeconds());
	}

	scheduleUpdateValue() {
		debounce(this, this.updateValue, 100);
	}

	updateFromValue() {
		const m = moment.duration(+(this.args.value || 0) * 1000);

		this.days = Math.floor(m.asDays());
		this.hours = m.hours();
		this.minutes = m.minutes();
		this.seconds = m.seconds();
	}

	@action
	scheduleUpdateFromValue() {
		once(this, this.updateFromValue);
	}

	@action
	focusOut() {
		this.scheduleUpdateValue();
	}

	@action
	forceInteger(key: 'days' | 'hours' | 'minutes' | 'seconds') {
		const value = this[key],
			intValue = String(value).replace(/[^0-9]/g, '');
		if (value !== intValue) {
			this[key] = intValue;
		}
	}
	<template>
		<div
			class="input-duration"
			{{didInsert this.scheduleUpdateFromValue}}
			{{didUpdate this.scheduleUpdateFromValue @forceUpdateFromValue}}
		>
			<div class="part">
				<FormInputComponent
					name="days"
					@value={{this.days}}
					@focusOut={{this.focusOut}}
					@input={{fn this.forceInteger "days"}}
					@theme={{@theme}}
					@size={{@size}}
					disabled={{@isDisabled}}
					required={{@isRequired}}
				/>
				<span>d</span>
			</div>

			<div class="part">
				<FormInputComponent
					name="hours"
					@value={{this.hours}}
					@focusOut={{this.focusOut}}
					@input={{fn this.forceInteger "hours"}}
					@theme={{@theme}}
					@size={{@size}}
					disabled={{@isDisabled}}
					required={{@isRequired}}
				/>
				<span>h</span>
			</div>

			<div class="part">
				<FormInputComponent
					name="minutes"
					@value={{this.minutes}}
					@focusOut={{this.focusOut}}
					@input={{fn this.forceInteger "minutes"}}
					@theme={{@theme}}
					@size={{@size}}
					disabled={{@isDisabled}}
					required={{@isRequired}}
				/>
				<span>m</span>
			</div>

			<div class="part">
				<FormInputComponent
					name="seconds"
					@value={{this.seconds}}
					@focusOut={{this.focusOut}}
					@input={{fn this.forceInteger "seconds"}}
					@theme={{@theme}}
					@size={{@size}}
					disabled={{@isDisabled}}
					required={{@isRequired}}
				/>
				<span>s</span>
			</div>
		</div>
	</template>
}
